import { useState, useEffect } from 'react';
import { useI18n } from '../../i18n/i18n.jsx';
import { useTheme } from '../../hooks/useTheme.jsx';
import FormInput from './FormInput';
import { emailConfig } from '../../config/email.js';

const ContactFormModal = ({ isOpen, onClose, defaultSubject = 'general' }) => {
  const { t } = useI18n();
  const { isDark } = useTheme();
  
  const [formData, setFormData] = useState({
    subject: defaultSubject,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Update subject when defaultSubject changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({ ...prev, subject: defaultSubject }));
    }
  }, [isOpen, defaultSubject]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        subject: 'general',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setErrors({});
      setSubmitStatus(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('contact.form.errors.firstName');
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('contact.form.errors.lastName');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('contact.form.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.errors.emailInvalid');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.errors.message');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch('https://edu.bdowur.com/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: t(`contact.form.subjects.${formData.subject}`),
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData({ 
          subject: 'general', 
          firstName: '', 
          lastName: '', 
          email: '', 
          phone: '', 
          message: '' 
        });
        
        // Close modal after 2 seconds
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      } else {
        setIsSubmitting(false);
        setSubmitStatus('error');
        console.error('API Error:', result.message);
        
        setTimeout(() => setSubmitStatus(null), 5000);
      }
      
    } catch (error) {
      console.error('Connection error:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
      
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" />
      
      {/* Modal Container */}
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-scale-in ${
        isDark ? 'bg-neutral-900 border border-neutral-700' : 'bg-white border border-neutral-200'
      }`}>
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors duration-200"
          aria-label="Close modal"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Modal Header */}
        <div className={`p-6 md:p-8 border-b ${
          isDark ? 'border-neutral-700' : 'border-neutral-200'
        }`}>
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-primary dark:text-white mb-2">
            {t('hero.cta')}
          </h2>
          <p className="text-sm md:text-base text-neutral-600 dark:text-neutral-300">
            {t('contact.subtitle')}
          </p>
        </div>

        {/* Modal Body */}
        <div className="p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            {/* Subject Dropdown */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                {t('contact.form.subject')}
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`w-full px-3 py-2 md:px-4 md:py-3 rounded-lg border transition-all duration-200 text-sm md:text-base ${
                  isDark 
                    ? 'bg-neutral-800 border-neutral-600 text-white focus:ring-2 focus:ring-accent' 
                    : 'bg-white border-neutral-300 text-neutral-900 focus:ring-2 focus:ring-primary'
                }`}
              >
                <option value="general">{t('contact.form.subjects.general')}</option>
                <option value="sales">{t('contact.form.subjects.sales')}</option>
                <option value="support">{t('contact.form.subjects.support')}</option>
                <option value="partnership">{t('contact.form.subjects.partnership')}</option>
                <option value="other">{t('contact.form.subjects.other')}</option>
              </select>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <FormInput
                label={t('contact.form.firstName')}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder={t('contact.form.firstNamePlaceholder')}
                required
                error={errors.firstName}
              />
              
              <FormInput
                label={t('contact.form.lastName')}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder={t('contact.form.lastNamePlaceholder')}
                required
                error={errors.lastName}
              />
            </div>
            
            {/* Email */}
            <FormInput
              label={t('contact.form.email')}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('contact.form.emailPlaceholder')}
              required
              error={errors.email}
            />
            
            {/* Phone */}
            <FormInput
              label={t('contact.form.phone')}
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('contact.form.phonePlaceholder')}
            />
            
            {/* Message */}
            <FormInput
              label={t('contact.form.message')}
              as="textarea"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t('contact.form.messagePlaceholder')}
              required
              error={errors.message}
              rows={4}
            />
            
            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`group relative w-full py-3 md:py-4 bg-primary text-white font-semibold rounded-xl overflow-hidden hover-lift shine-on-hover transition-all duration-300 text-sm md:text-base ${
                isSubmitting
                  ? 'opacity-70 cursor-not-allowed'
                  : 'shadow-xl'
              }`}
            >
              <span className="relative z-10">
                {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
              </span>
              {!isSubmitting && (
                <div className="absolute inset-0 bg-primary-light opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              )}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-3 md:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm md:text-base animate-fade-in">
                ✓ {t('contact.form.success')}
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="p-3 md:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm md:text-base animate-fade-in">
                ✕ {t('contact.form.error')}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactFormModal;
